/**
 * Wire values accepted by PatientCoverageSaveRequestValidator. These are PascalCase tokens stored
 * verbatim by the API — the display label never travels.
 */
export const COVERAGE_TYPES = ['MedicarePartA', 'MedicarePartB', 'MedicareAdvantage', 'Medicaid', 'Commercial', 'SelfPay', 'Other'] as const;

export const COVERAGE_STATUSES = ['Active', 'Inactive'] as const;

/** Field length limits, mirrored from the backend FluentValidation rules. */
export const PAYER_NAME_MAX = 200;
export const MEMBER_ID_MAX = 50;
export const SUBSCRIBER_ID_MAX = 50;
export const GROUP_NUMBER_MAX = 50;
export const PLAN_ID_MAX = 50;
export const PLAN_NAME_MAX = 100;

/**
 * Subscriber relationship values are HL7 v3 RoleCodes, not display text — the validator rejects
 * anything outside this set, so the label is display-only. FAMDEP is what the ONC b1 certification
 * scenario keys (item 67); it is emitted verbatim rather than defaulted to self.
 */
export const SUBSCRIBER_RELATIONSHIP_LABELS: Record<string, string> = {
    SELF: 'Self',
    SPS: 'Spouse',
    CHILD: 'Child',
    FAMDEP: 'Family dependent',
    OTH: 'Other',
};

/** Field length limits for the payer contact block, mirrored from the backend validator. */
export const PAYER_ADDRESS_LINE_MAX = 200;
export const PAYER_CITY_MAX = 100;
export const PAYER_STATE_MAX = 50;
export const PAYER_POSTAL_CODE_MAX = 20;
export const PAYER_COUNTRY_MAX = 50;
export const PAYER_PHONE_MAX = 30;

/**
 * NAIC — the assigning authority the USCDI v3 Payer Identifier is keyed under in the ONC b1 test data.
 * Pre-filled on the coverage form (new coverage, or an edit of one saved without a root); editable.
 */
export const NAIC_PAYER_IDENTIFIER_ROOT = '2.16.840.1.113883.6.300';
export const PAYER_IDENTIFIER_ROOT_MAX = 64;
export const PAYER_IDENTIFIER_EXTENSION_MAX = 64;

/** Mirror PatientCoverageSaveRequestValidator.OidPattern / NaicCompanyCodePattern. */
export const OID_PATTERN = /^[0-2](\.(0|[1-9][0-9]*))+$/;
export const NAIC_CODE_PATTERN = /^[0-9]{5}$/;

/** The payer search reads a directory built from every patient's coverage, so it waits for a real term. */
export const PAYER_SEARCH_DEBOUNCE_MS = 250;
export const PAYER_SEARCH_TAKE = 8;
export const PAYER_SEARCH_MIN_LENGTH = 2;

/** "Payer ID 2.16.840.1.113883.6.300 (NAIC) · 12345" — the root itself is shown so it can be checked against the spec. */
export function payerIdentifierLabel(root: string | null | undefined, extension: string | null | undefined): string {
    if (!root) {
        return '';
    }

    const authority = root === NAIC_PAYER_IDENTIFIER_ROOT ? `${root} (NAIC)` : root;
    return extension ? `Payer ID ${authority} · ${extension}` : `Payer ID ${authority}`;
}

type PayerSummary = {
    payerName?: string | null;
    payerIdentifierRoot?: string | null;
    payerIdentifierExtension?: string | null;
    payerCity?: string | null;
    payerState?: string | null;
};

/** One search suggestion: the name, then what tells two similar payers apart. */
export function payerOptionLabel(payer: PayerSummary): string {
    const place = [payer.payerCity, payer.payerState].filter(Boolean).join(', ');
    return [payer.payerName, payerIdentifierLabel(payer.payerIdentifierRoot, payer.payerIdentifierExtension), place].filter(Boolean).join(' — ');
}

export const RANK_LABELS: Record<number, string> = {
    1: 'Primary',
    2: 'Secondary',
    3: 'Tertiary',
};

/** "MedicarePartA" -> "Medicare Part A". Display only; the token is what the API stores. */
export function coverageTypeLabel(coverageType: string | null | undefined): string {
    if (!coverageType) {
        return '';
    }

    return coverageType.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** Falls back to the raw RoleCode so a value added server-side still renders something meaningful. */
export function relationshipLabel(roleCode: string | null | undefined): string {
    if (!roleCode) {
        return '';
    }

    return SUBSCRIBER_RELATIONSHIP_LABELS[roleCode] ?? roleCode;
}

export function rankLabel(rank: number | null | undefined): string {
    if (rank == null) {
        return '';
    }

    return RANK_LABELS[rank] ?? String(rank);
}

const toOption = (value: string, label: string) => ({ value, label, name: `${label}_${value}` });

export const COVERAGE_TYPE_OPTIONS = COVERAGE_TYPES.map((t) => toOption(t, coverageTypeLabel(t)));

export const COVERAGE_STATUS_OPTIONS = COVERAGE_STATUSES.map((s) => toOption(s, s));

export const SUBSCRIBER_RELATIONSHIP_OPTIONS = Object.entries(SUBSCRIBER_RELATIONSHIP_LABELS).map(([code, label]) => toOption(code, label));

export const RANK_OPTIONS = Object.entries(RANK_LABELS).map(([rank, label]) => ({ value: Number(rank), label, name: `${label}_${rank}` }));
